import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  Battery,
  CheckCircle2,
  Clock,
  LocateFixed,
  MapPin,
  Plus,
  RefreshCw,
  ShieldCheck,
  Smartphone,
  Square,
} from 'lucide-react';
import { format } from 'date-fns';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import { positionService, vehicleService } from '../services/api';
import { useVehicleStore } from '../store';
import { toast } from 'react-toastify';

const emptyStatus = {
  label: 'Aguardando inicio',
  tone: 'badge-warning',
};

const toRadians = (degrees) => degrees * (Math.PI / 180);

const distanceInMeters = (start, end) => {
  if (!start || !end) return Infinity;

  const earthRadius = 6371000;
  const latDelta = toRadians(end.latitude - start.latitude);
  const lonDelta = toRadians(end.longitude - start.longitude);
  const startLat = toRadians(start.latitude);
  const endLat = toRadians(end.latitude);
  const a = Math.sin(latDelta / 2) ** 2
    + Math.cos(startLat) * Math.cos(endLat) * Math.sin(lonDelta / 2) ** 2;

  return earthRadius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const getBatteryLevel = async () => {
  if (!navigator.getBattery) return null;

  try {
    const battery = await navigator.getBattery();
    return Math.round(battery.level * 100);
  } catch (error) {
    return null;
  }
};

const MobileTracking = () => {
  const { vehicles, setVehicles, addVehicle, updateVehicle, setLoading, loading } = useVehicleStore();
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [consent, setConsent] = useState(false);
  const [tracking, setTracking] = useState(false);
  const [savingDevice, setSavingDevice] = useState(false);
  const [lastPosition, setLastPosition] = useState(null);
  const [lastSentAt, setLastSentAt] = useState(null);
  const [status, setStatus] = useState(emptyStatus);
  const watchIdRef = useRef(null);
  const lastSentAtRef = useRef(0);
  const lastCoordsRef = useRef(null);

  const mobileVehicles = useMemo(() => (
    vehicles.filter((vehicle) => (
      vehicle.tracker_model === 'Celular autorizado'
      || vehicle.icon === 'smartphone'
      || vehicle.type === 'other'
    ))
  ), [vehicles]);

  const selectedVehicle = useMemo(() => (
    vehicles.find((vehicle) => vehicle.id === selectedVehicleId)
  ), [selectedVehicleId, vehicles]);

  useEffect(() => {
    loadVehicles();

    return () => {
      stopTracking();
    };
  }, []);

  const loadVehicles = async () => {
    setLoading(true);

    try {
      const response = await vehicleService.getAll();
      setVehicles(response.data.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao carregar cadastros');
    } finally {
      setLoading(false);
    }
  };

  const createMobileDevice = async () => {
    const name = deviceName.trim();

    if (!name) {
      toast.error('Informe o nome do celular');
      return;
    }

    setSavingDevice(true);

    try {
      const suffix = Date.now().toString().slice(-6);
      const response = await vehicleService.create({
        name,
        plate: `CEL-${suffix}`,
        brand: 'Celular',
        model: 'Autorizado',
        type: 'other',
        tracker_model: 'Celular autorizado',
        icon: 'smartphone',
        notes: 'Cadastro usado para rastreamento de celular com autorizacao do cliente.',
      });

      addVehicle(response.data.data);
      setSelectedVehicleId(response.data.data.id);
      setDeviceName('');
      toast.success('Celular cadastrado');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao cadastrar celular');
    } finally {
      setSavingDevice(false);
    }
  };

  const sendMobilePosition = async (position) => {
    if (!selectedVehicleId) return;

    const coords = position.coords;
    const currentCoords = {
      latitude: coords.latitude,
      longitude: coords.longitude,
    };
    const now = Date.now();
    const movedMeters = distanceInMeters(lastCoordsRef.current, currentCoords);

    if (lastSentAtRef.current && now - lastSentAtRef.current < 10000 && movedMeters < 15) {
      return;
    }

    const battery = await getBatteryLevel();
    const speedKmh = Number.isFinite(coords.speed) && coords.speed !== null
      ? Math.max(0, coords.speed * 3.6)
      : 0;

    const payload = {
      vehicle_id: selectedVehicleId,
      latitude: coords.latitude,
      longitude: coords.longitude,
      speed: speedKmh,
      heading: Number.isFinite(coords.heading) ? coords.heading : null,
      altitude: Number.isFinite(coords.altitude) ? coords.altitude : null,
      accuracy: Number.isFinite(coords.accuracy) ? coords.accuracy : null,
      battery,
      address: 'Celular autorizado pelo cliente',
      device_time: new Date(position.timestamp || Date.now()).toISOString(),
      consent: true,
      mobile_user_agent: navigator.userAgent,
    };

    const response = await positionService.mobile(payload);
    const savedPosition = response.data.data.position;

    lastSentAtRef.current = now;
    lastCoordsRef.current = currentCoords;
    setLastPosition(savedPosition);
    setLastSentAt(new Date());
    updateVehicle(selectedVehicleId, {
      positions: [savedPosition],
      last_update: savedPosition.device_time || new Date().toISOString(),
    });
    setStatus({
      label: 'Enviando localizacao',
      tone: 'badge-success',
    });
  };

  const startTracking = () => {
    if (!selectedVehicleId) {
      toast.error('Selecione ou cadastre um celular');
      return;
    }

    if (!consent) {
      toast.error('Confirme a autorizacao do cliente');
      return;
    }

    if (!navigator.geolocation) {
      toast.error('Este aparelho nao suporta localizacao pelo navegador');
      return;
    }

    setStatus({
      label: 'Solicitando permissao',
      tone: 'badge-warning',
    });

    const watchId = navigator.geolocation.watchPosition(
      async (position) => {
        try {
          await sendMobilePosition(position);
        } catch (error) {
          setStatus({
            label: 'Falha no envio',
            tone: 'badge-danger',
          });
          toast.error(error.response?.data?.message || 'Erro ao enviar localizacao');
        }
      },
      (error) => {
        const message = error.code === error.PERMISSION_DENIED
          ? 'Permissao de localizacao negada'
          : 'Nao foi possivel obter localizacao';

        setStatus({
          label: message,
          tone: 'badge-danger',
        });
        setTracking(false);
        toast.error(message);
      },
      {
        enableHighAccuracy: true,
        maximumAge: 5000,
        timeout: 20000,
      }
    );

    watchIdRef.current = watchId;
    setTracking(true);
    toast.success('Rastreamento do celular iniciado');
  };

  const stopTracking = () => {
    if (watchIdRef.current !== null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }

    setTracking(false);
    setStatus(emptyStatus);
  };

  return (
    <div className="flex h-screen bg-dark-950">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Rastrear celular</h1>
              <p className="text-dark-400">Localizacao enviada somente com autorizacao ativa no aparelho.</p>
            </div>

            <button
              type="button"
              onClick={loadVehicles}
              className="btn-secondary flex items-center justify-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Atualizar
            </button>
          </div>

          <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="card">
              <div className="mb-3 flex items-center gap-3 text-dark-300">
                <ShieldCheck className="h-5 w-5 text-primary-400" />
                Autorizacao
              </div>
              <span className={`badge ${consent ? 'badge-success' : 'badge-warning'}`}>
                {consent ? 'Confirmada' : 'Pendente'}
              </span>
            </div>

            <div className="card">
              <div className="mb-3 flex items-center gap-3 text-dark-300">
                <Activity className="h-5 w-5 text-primary-400" />
                Status
              </div>
              <span className={`badge ${status.tone}`}>{status.label}</span>
            </div>

            <div className="card">
              <div className="mb-3 flex items-center gap-3 text-dark-300">
                <Clock className="h-5 w-5 text-primary-400" />
                Ultimo envio
              </div>
              <p className="text-sm text-white">
                {lastSentAt ? format(lastSentAt, 'dd/MM/yyyy HH:mm:ss') : 'Nenhum envio'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[420px_1fr]">
            <section className="space-y-6">
              <div className="card">
                <div className="mb-5 flex items-center gap-3">
                  <Smartphone className="h-6 w-6 text-primary-400" />
                  <div>
                    <h2 className="text-lg font-bold text-white">Celular do cliente</h2>
                    <p className="text-sm text-dark-400">Use um cadastro existente ou crie um novo.</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-dark-300">Selecionar cadastro</label>
                    <select
                      value={selectedVehicleId}
                      onChange={(event) => setSelectedVehicleId(event.target.value)}
                      className="input-field"
                      disabled={tracking}
                    >
                      <option value="">Selecione...</option>
                      {(mobileVehicles.length > 0 ? mobileVehicles : vehicles).map((vehicle) => (
                        <option key={vehicle.id} value={vehicle.id}>
                          {vehicle.name} - {vehicle.plate}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="rounded-lg border border-dark-700 bg-dark-900 p-4">
                    <label className="mb-2 block text-sm font-medium text-dark-300">Novo celular</label>
                    <div className="flex flex-col gap-3 sm:flex-row">
                      <input
                        value={deviceName}
                        onChange={(event) => setDeviceName(event.target.value)}
                        className="input-field"
                        placeholder="Ex: Celular Joao"
                        disabled={tracking || savingDevice}
                      />
                      <button
                        type="button"
                        onClick={createMobileDevice}
                        disabled={tracking || savingDevice}
                        className="btn-secondary flex shrink-0 items-center justify-center gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        {savingDevice ? 'Criando...' : 'Criar'}
                      </button>
                    </div>
                  </div>

                  <label className="flex items-start gap-3 rounded-lg border border-dark-700 bg-dark-900 p-4 text-sm text-dark-300">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={(event) => setConsent(event.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-dark-600 accent-primary-500"
                      disabled={tracking}
                    />
                    <span>
                      Confirmo que o cliente autorizou o uso da localizacao deste celular e sabe que pode parar o envio.
                    </span>
                  </label>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <button
                      type="button"
                      onClick={startTracking}
                      disabled={tracking || loading}
                      className="btn-primary flex items-center justify-center gap-2"
                    >
                      <LocateFixed className="h-4 w-4" />
                      Iniciar
                    </button>
                    <button
                      type="button"
                      onClick={stopTracking}
                      disabled={!tracking}
                      className="btn-danger flex items-center justify-center gap-2"
                    >
                      <Square className="h-4 w-4" />
                      Parar
                    </button>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="mb-3 flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-warning-400" />
                  <h2 className="text-lg font-bold text-white">Privacidade</h2>
                </div>
                <p className="text-sm leading-6 text-dark-300">
                  Nao existe rastreamento oculto nesta tela. O envio depende da permissao do aparelho, fica ativo somente apos iniciar e pode ser parado pelo botao acima.
                </p>
              </div>
            </section>

            <section className="card">
              <div className="mb-6 flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-bold text-white">Ultima localizacao enviada</h2>
                  <p className="text-sm text-dark-400">
                    {selectedVehicle ? selectedVehicle.name : 'Nenhum celular selecionado'}
                  </p>
                </div>
                {tracking ? (
                  <span className="badge badge-success">Ativo</span>
                ) : (
                  <span className="badge badge-warning">Parado</span>
                )}
              </div>

              {!lastPosition ? (
                <div className="flex min-h-[360px] flex-col items-center justify-center rounded-lg border border-dashed border-dark-700 text-center text-dark-400">
                  <Smartphone className="mb-3 h-12 w-12 opacity-60" />
                  <p>Nenhuma posicao enviada nesta sessao.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="rounded-lg border border-dark-700 bg-dark-900 p-5">
                    <div className="mb-2 flex items-center gap-2 text-sm text-dark-400">
                      <MapPin className="h-4 w-4" />
                      Coordenadas
                    </div>
                    <p className="text-lg font-semibold text-white">
                      {Number(lastPosition.latitude).toFixed(6)}, {Number(lastPosition.longitude).toFixed(6)}
                    </p>
                  </div>

                  <div className="rounded-lg border border-dark-700 bg-dark-900 p-5">
                    <div className="mb-2 flex items-center gap-2 text-sm text-dark-400">
                      <LocateFixed className="h-4 w-4" />
                      Precisao
                    </div>
                    <p className="text-lg font-semibold text-white">
                      {lastPosition.accuracy ? `${Number(lastPosition.accuracy).toFixed(0)} m` : 'Nao informado'}
                    </p>
                  </div>

                  <div className="rounded-lg border border-dark-700 bg-dark-900 p-5">
                    <div className="mb-2 flex items-center gap-2 text-sm text-dark-400">
                      <Activity className="h-4 w-4" />
                      Velocidade
                    </div>
                    <p className="text-lg font-semibold text-white">
                      {Number(lastPosition.speed || 0).toFixed(0)} km/h
                    </p>
                  </div>

                  <div className="rounded-lg border border-dark-700 bg-dark-900 p-5">
                    <div className="mb-2 flex items-center gap-2 text-sm text-dark-400">
                      <Battery className="h-4 w-4" />
                      Bateria
                    </div>
                    <p className="text-lg font-semibold text-white">
                      {lastPosition.battery ?? 'Nao informado'}{lastPosition.battery ? '%' : ''}
                    </p>
                  </div>

                  <div className="rounded-lg border border-dark-700 bg-dark-900 p-5 md:col-span-2">
                    <div className="mb-2 flex items-center gap-2 text-sm text-dark-400">
                      <CheckCircle2 className="h-4 w-4" />
                      Registro
                    </div>
                    <p className="text-white">
                      {lastPosition.device_time ? format(new Date(lastPosition.device_time), 'dd/MM/yyyy HH:mm:ss') : 'Sem horario'}
                    </p>
                  </div>
                </div>
              )}
            </section>
          </div>
        </main>
      </div>
    </div>
  );
};

export default MobileTracking;
